import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

type ProgressType = 'linear' | 'circular';
type ProgressColor = 'primary' | 'success' | 'warning' | 'error' | 'info';

interface CircularCustomSize {
  radius: number;
  stroke: number;
}

interface ProgressBarProps extends ForwardedHTMLProps {
  value: number;
  max?: number;
  type?: ProgressType;
  size?: ComponentSize;
  customSize?: CircularCustomSize;
  color?: ProgressColor;
  showLabel?: boolean;
  label?: string;
  labelPosition?: 'top' | 'right' | 'inside';
  indeterminate?: boolean;
  animated?: boolean;
  striped?: boolean;
  className?: string;
}

const linearSizeConfig: Record<ComponentSize, string> = {
  xs: 'h-1',
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
  xl: 'h-4',
};

const circularSizeConfig: Record<ComponentSize, { size: number; stroke: number; svgSize: string }> = {
  xs: { size: 16, stroke: 2, svgSize: 'w-8 h-8' },
  sm: { size: 18, stroke: 3, svgSize: 'w-10 h-10' },
  md: { size: 20, stroke: 4, svgSize: 'w-12 h-12' },
  lg: { size: 24, stroke: 4, svgSize: 'w-16 h-16' },
  xl: { size: 28, stroke: 5, svgSize: 'w-20 h-20' },
};

const colorClasses: Record<ProgressColor, { bg: string; track: string }> = {
  primary: { bg: 'bg-primary', track: 'bg-slate-200 dark:bg-slate-700' },
  success: { bg: 'bg-green-500', track: 'bg-green-100 dark:bg-green-900/30' },
  warning: { bg: 'bg-amber-500', track: 'bg-amber-100 dark:bg-amber-900/30' },
  error: { bg: 'bg-red-500', track: 'bg-red-100 dark:bg-red-900/30' },
  info: { bg: 'bg-blue-500', track: 'bg-blue-100 dark:bg-blue-900/30' },
};

const strokeClasses: Record<ProgressColor, string> = {
  primary: 'stroke-primary',
  success: 'stroke-green-500',
  warning: 'stroke-amber-500',
  error: 'stroke-red-500',
  info: 'stroke-blue-500',
};

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value,
      max = 100,
      type = 'linear',
      size = 'md',
      customSize,
      color = 'primary',
      showLabel = false,
      label,
      labelPosition = 'top',
      indeterminate = false,
      animated = false,
      striped = false,
      className,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);

    const percentage = useMemo(() => {
      if (indeterminate) return 100;
      return Math.min(Math.max((value / max) * 100, 0), 100);
    }, [value, max, indeterminate]);

    const [displayValue, setDisplayValue] = useState(percentage);
    const displayRef = useRef(percentage);
    displayRef.current = displayValue;

    useEffect(() => {
      if (indeterminate) return;
      const start = displayRef.current;
      const end = percentage;
      const duration = 1500;
      const startTime = performance.now();
      let rafId: number;
      let cancelled = false;

      const update = (now: number) => {
        if (cancelled) return;
        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - (1 - t) * (1 - t);
        const newVal = start + (end - start) * eased;
        setDisplayValue(newVal);
        displayRef.current = newVal;
        if (t < 1) rafId = requestAnimationFrame(update);
      };
      rafId = requestAnimationFrame(update);
      return () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
      };
    }, [percentage, indeterminate]);

    useEffect(() => {
      if (indeterminate) setDisplayValue(0);
    }, [indeterminate]);

    const colorConfig = colorClasses[color];
    const displayLabel = label || `${Math.round(displayValue)}%`;

    if (type === 'circular') {
      const isCustom = !!customSize;
      const config = circularSizeConfig[size];
      const radius = isCustom ? customSize.radius : config.size;
      const strokeWidth = isCustom ? customSize.stroke : config.stroke;
      const circumference = 2 * Math.PI * radius;
      const arcLength = circumference * 0.25;
      const offset = indeterminate ? 0 : circumference - (percentage / 100) * circumference;
      const center = radius + strokeWidth;
      const viewBox = (radius + strokeWidth) * 2;

      const labelSizeClass = isCustom
        ? 'text-3xl'
        : cn(
            size === 'xs' && 'text-[8px]',
            size === 'sm' && 'text-[10px]',
            size === 'md' && 'text-xs',
            size === 'lg' && 'text-sm',
            size === 'xl' && 'text-base'
          );

      return (
        <span
          ref={ref}
          className={cn('relative inline-flex items-center justify-center', className)}
          role="progressbar"
          aria-valuenow={indeterminate ? undefined : Math.round(percentage)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label || 'Progress'}
          {...forwardedProps}
          {...props}
        >
          <svg
            className={cn(!isCustom && config.svgSize, 'transform -rotate-90')}
            {...(isCustom ? { width: viewBox, height: viewBox } : {})}
            viewBox={`0 0 ${viewBox} ${viewBox}`}
          >
            <circle
              cx={center}
              cy={center}
              r={radius}
              className={colorConfig.track}
              stroke="currentColor"
              strokeWidth={strokeWidth}
              fill="none"
            />
            <circle
              cx={center}
              cy={center}
              r={radius}
              strokeWidth={strokeWidth}
              fill="none"
              strokeDasharray={indeterminate ? `${arcLength} ${circumference - arcLength}` : circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className={cn(
                strokeClasses[color],
                indeterminate
                  ? 'animate-circular-progress-indeterminate opacity-90'
                  : 'transition-all duration-[1500ms] ease-out'
              )}
            />
          </svg>
          {showLabel && !indeterminate && (
            <span className={cn('absolute font-bold tabular-nums', labelSizeClass, tef.colors.foreground)}>
              {displayLabel}
            </span>
          )}
        </span>
      );
    }

    const heightClass = linearSizeConfig[size];
    const showInsideLabel = showLabel && labelPosition === 'inside' && size !== 'xs' && size !== 'sm';

    return (
      <section
        ref={ref}
        className={cn('w-full', className)}
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : Math.round(percentage)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label || 'Progress'}
        {...forwardedProps}
        {...props}
      >
        {showLabel && labelPosition === 'top' && (
          <span
            className={cn(
              'flex justify-between mb-1.5',
              size === 'xs' || size === 'sm' ? 'text-xs' : 'text-sm',
              tef.colors.foregroundSecondary
            )}
          >
            <span>{label || 'Progress'}</span>
            {!indeterminate && <span>{displayLabel}</span>}
          </span>
        )}

        <span className={cn('flex items-center gap-3', showLabel && labelPosition === 'right' && 'flex-row')}>
          <span className={cn('flex-1 rounded-full overflow-hidden', heightClass, colorConfig.track)}>
            <span
              className={cn(
                'block h-full rounded-full',
                'transition-all duration-300',
                colorConfig.bg,
                striped && 'bg-[length:1rem_1rem] bg-gradient-to-r from-transparent via-white/20 to-transparent',
                animated && striped && 'animate-[progress-stripes_1s_linear_infinite]',
                indeterminate && 'animate-[progress-indeterminate_1.5s_ease-in-out_infinite]',
                showInsideLabel && 'flex items-center justify-end pr-2'
              )}
              style={{ width: indeterminate ? '50%' : `${percentage}%` }}
            >
              {showInsideLabel && !indeterminate && (
                <span className="text-[10px] text-white font-medium">{displayLabel}</span>
              )}
            </span>
          </span>

          {showLabel && labelPosition === 'right' && !indeterminate && (
            <span
              className={cn(
                'flex-shrink-0 tabular-nums',
                size === 'xs' || size === 'sm' ? 'text-xs' : 'text-sm',
                tef.colors.foreground
              )}
            >
              {displayLabel}
            </span>
          )}
        </span>
      </section>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
